import type { LngLat, Shape } from "../adapters/types";

/** En deçà, le plan est considéré comme déjà aligné sur le nord : rien à faire pivoter. */
const MIN_ROTATION_DEG = 0.5;
/** Une orientation secondaire n'est retenue que si elle pèse au moins cette part de la principale. */
const SECONDARY_SHARE = 0.2;
/** Écart minimal entre deux orientations retenues, et demi-largeur de la moyenne autour d'un pic. */
const PEAK_SPREAD_DEG = 4;

export interface PlanFrame {
  /** Angle, en degrés, dont le plan est tourné par rapport au nord, entre -45 et 45. */
  rotationDeg: number;
  toFrame(point: LngLat): LngLat;
  fromFrame(point: LngLat): LngLat;
  shapes: Shape[];
}

/** Écart entre deux angles à 90° près. */
function quarterDistance(a: number, b: number): number {
  const d = (((a - b) % 90) + 135) % 90 - 45;
  return Math.abs(d);
}

/**
 * Orientations des bords des obstacles, à 90° près, de la plus présente à la moins présente : celle
 * des murs et des rangées de stands d'un plan dessiné de biais, puis celle d'un hall tourné autrement.
 * Histogramme au degré pondéré par la longueur des bords, calculé en mètres pour que la latitude ne
 * fausse pas les angles ; chaque pic est affiné par la moyenne des bords qui l'entourent.
 */
export function planRotationsDeg(shapes: Shape[]): number[] {
  const edges: { angle: number; length: number }[] = [];
  for (const shape of shapes) {
    if (shape.walkable || shape.shapeKind === "circle" || shape.shapeKind === "marker" || shape.shapeKind === "decor") continue;
    const ring = shape.coordinates;
    if (ring.length < 2) continue;
    const cos = Math.cos((ring[0]![1] * Math.PI) / 180);
    for (let i = 0; i < ring.length - 1; i++) {
      const dx = (ring[i + 1]![0] - ring[i]![0]) * cos;
      const dy = ring[i + 1]![1] - ring[i]![1];
      const length = Math.hypot(dx, dy);
      if (length) edges.push({ angle: ((((Math.atan2(dy, dx) * 180) / Math.PI) % 90) + 135) % 90 - 45, length });
    }
  }
  if (!edges.length) return [];

  const histogram = new Array(90).fill(0);
  for (const { angle, length } of edges) histogram[(Math.round(angle) + 90) % 90] += length;
  const smoothed = histogram.map((_, i) => histogram[(i + 89) % 90] + histogram[i] * 2 + histogram[(i + 1) % 90]);

  const peaks: { angle: number; weight: number }[] = [];
  for (const i of [...smoothed.keys()].sort((a, b) => smoothed[b] - smoothed[a])) {
    const bin = i > 45 ? i - 90 : i;
    if (peaks.some((peak) => quarterDistance(peak.angle, bin) < PEAK_SPREAD_DEG * 2)) continue;
    if (peaks.length && smoothed[i] < peaks[0]!.weight * SECONDARY_SHARE) break;
    peaks.push({ angle: bin, weight: smoothed[i] });
    if (peaks.length === 2) break;
  }

  return peaks.map(({ angle }) => {
    let sum = 0;
    let weight = 0;
    for (const edge of edges) {
      if (quarterDistance(edge.angle, angle) > PEAK_SPREAD_DEG) continue;
      const d = (((edge.angle - angle) % 90) + 135) % 90 - 45;
      sum += (angle + d) * edge.length;
      weight += edge.length;
    }
    return weight ? sum / weight : angle;
  });
}

/**
 * Repères où le plan est remis droit, un par orientation dominante : la grille de l'itinéraire,
 * alignée sur le nord, suit alors les murs et les rangées de stands au lieu de les couper en
 * escalier — ce qui bouchait les couloirs étroits d'un plan tourné. La rotation se fait en mètres
 * autour d'un point du plan, et les coordonnées gardent leur échelle en degrés : les distances et les
 * cercles restent justes. `null` pour une orientation déjà alignée sur le nord : le plan tel quel.
 */
export function planFrames(shapes: Shape[]): (PlanFrame | null)[] {
  const origin = shapes.find((shape) => shape.coordinates.length)?.coordinates[0];
  const rotations = planRotationsDeg(shapes);
  if (!origin || !rotations.length) return [null];
  const scale = Math.cos((origin[1] * Math.PI) / 180);
  const rotate = (point: LngLat, angleDeg: number): LngLat => {
    const angle = (angleDeg * Math.PI) / 180;
    const x = (point[0] - origin[0]) * scale;
    const y = point[1] - origin[1];
    return [origin[0] + (x * Math.cos(angle) - y * Math.sin(angle)) / scale, origin[1] + x * Math.sin(angle) + y * Math.cos(angle)];
  };

  return rotations.map((rotationDeg) => {
    if (Math.abs(rotationDeg) < MIN_ROTATION_DEG) return null;
    const toFrame = (point: LngLat) => rotate(point, -rotationDeg);
    const fromFrame = (point: LngLat) => rotate(point, rotationDeg);
    return {
      rotationDeg,
      toFrame,
      fromFrame,
      shapes: shapes.map((shape) => ({
        ...shape,
        coordinates: shape.coordinates.map(toFrame),
        ...(shape.holes ? { holes: shape.holes.map((hole) => hole.map(toFrame)) } : {}),
      })),
    };
  });
}
